/*  ─────────────────────────────────────────────────────────────────────────
    Site Settings · stateful subsection components
    Phase 2 extraction from Site.jsx (was 2,027 lines after Phase 1; the
    remaining bulk was the 10 section bodies below). Each export here is a
    self-contained subsection that consumes `s` (the live site_settings
    draft) + `set` (the parent's `set(key, value)` updater) — or, for a
    couple of sections (CustomDomainSection), the raw `setS` so they can
    do their own multi-key updates.

    Sections (top-down):
      · EmailSenderSection      — outgoing email sender + sandbox warning
      · EmailTestPanel          — "send a test email" panel
      · MigrationPanel          — export/import site JSON between envs
      · BrandAssetsSection      — logo / cover logo / favicon
      · AssetUploader           — generic image upload tile (used by ↑)
      · LiveLinksPanel          — public booking URL surface
      · LiveLinkRow             — single row inside ↑
      · CustomDomainSection     — Cloudflare for SaaS hostname + DNS guide
      · TemplateSection         — Editorial / Bold / Minimal / Journal cards
      · ThemeSection            — theme picker + per-color overrides + saved palettes
      · IdentityVoiceSection    — studio name / tagline / wordmark / typeface

    Site.jsx is now the orchestrator only: data fetching, save bar, tab
    switching, and the homepage / portfolio / booking copy-deck inputs.
    ───────────────────────────────────────────────────────────────────── */
import { useCallback, useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Upload, Loader2, X, Trash2, ImagePlus, RotateCcw, Sparkles, Copy,
  Check, RefreshCw, AlertTriangle, Send, Globe, ExternalLink, Unlink,
  CheckCircle2, ChevronDown, Link as LinkIcon, Plus,
} from "lucide-react";
import { TEMPLATES } from "@/templates/registry";
import { THEMES, THEME_KEYS, DEFAULT_THEME_ID, getThemeById, resolvePalette } from "@/lib/themes";
import { applyTheme } from "@/lib/themeApplier";
import { useAdminChromeMode } from "@/hooks/useAdminChromeMode";
import {
  TYPEFACE_PAIRS, SUGGESTED_THEMES,
  Section, Field, CopyField, AccentColorPicker,
  shiftHexLightness,
} from "./_shared";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export function EmailSenderSection({ s, set }) {
  const sender = (s.sender_email || "").trim();
  const onSandbox = !sender || sender.toLowerCase().includes("resend.dev");

  return (
    <section className="bg-card border border-border p-7" data-testid="email-sender-section">
      <div className="mb-5">
        <div className="eyebrow mb-2">Outgoing email</div>
        <h2 className="font-serif text-2xl">Sender address</h2>
        <p className="text-sm text-muted-foreground mt-1">
          The "From" address on every email your studio sends. Must be on a domain you've verified in Resend.
        </p>
      </div>

      {onSandbox && (
        <div className="mb-5 flex items-start gap-3 border border-amber-500/40 bg-amber-500/10 text-amber-200 px-4 py-3 text-sm" data-testid="sender-sandbox-warning">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <div>
            <strong>Customer emails are restricted.</strong> While the sender address is empty or on Resend's sandbox domain
            (<code className="bg-amber-100 px-1">onboarding@resend.dev</code>), Resend will only deliver to your own
            account email. To send to actual clients, set the sender to an address on your verified domain
            (e.g. <code className="bg-amber-100 px-1">bookings@allure-image.com</code>) and click <strong>Save changes</strong>.
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-6">
        <Field label="Sender email (must be on a domain verified in Resend)">
          <input
            value={s.sender_email || ""}
            onChange={(e) => set("sender_email", e.target.value)}
            placeholder="bookings@allure-image.com"
            className="input"
            data-testid="sender-email-setting"
          />
        </Field>
        <Field label="Sender display name (optional)">
          <input
            value={s.sender_name || ""}
            onChange={(e) => set("sender_name", e.target.value)}
            placeholder="Allure Image"
            className="input"
            data-testid="sender-name-setting"
          />
        </Field>
      </div>

      {/* BOOKING NOTIFICATION RECIPIENT — separated from sender_email so the
          studio can route inbound booking heads-ups to a shared inbox
          ("bookings@studio.com", "team@studio.com") without rotating the
          admin login email. Blank = falls back to the auth/owner email. */}
      <div className="mt-6 pt-6 border-t border-border">
        <div className="mb-3">
          <h3 className="font-serif text-lg">Booking notifications go to</h3>
          <p className="text-xs text-muted-foreground mt-1 max-w-2xl">
            Where new-booking heads-up emails land. Independent of your admin login email — set this to a shared inbox if you want bookings routed there. Leave blank to use your account email.
          </p>
        </div>
        <Field label="Booking notification email (optional)">
          <input
            type="email"
            value={s.booking_notification_email || ""}
            onChange={(e) => set("booking_notification_email", e.target.value)}
            placeholder="bookings@your-studio.com"
            className="input"
            data-testid="booking-notification-email-setting"
          />
        </Field>
      </div>

      <p className="text-xs text-muted-foreground mt-4">
        Changes go live the moment you click <strong>Save changes</strong>. No redeploy needed.
        Leave blank to fall back to the server's <code>SENDER_EMAIL</code> environment variable.
      </p>
    </section>
  );
}

export function EmailTestPanel() {
  const [testTo, setTestTo] = useState("");
  const [sending, setSending] = useState(false);

  async function sendTest() {
    if (!testTo || !testTo.includes("@")) return toast.error("Enter a valid email to send to");
    setSending(true);
    try {
      await api.post("/admin/config/test-email", { to: testTo });
      toast.success(`Test email sent to ${testTo}. Check inbox (and spam).`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Test email failed");
    } finally {
      setSending(false);
    }
  }

  return (
    <section className="bg-card border border-border p-7" data-testid="email-test-section">
      <div className="mb-5">
        <h2 className="font-serif text-2xl">Send a test email</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Sends a tiny email so you can confirm delivery is working. Sender details come from your server configuration.
        </p>
      </div>
      <div className="flex flex-col sm:flex-row gap-2">
        <input
          value={testTo}
          onChange={(e) => setTestTo(e.target.value)}
          placeholder="you@example.com"
          className="flex-1 bg-transparent border-b border-border py-2 outline-none focus:border-foreground text-sm"
          data-testid="test-email-to"
        />
        <button
          onClick={sendTest}
          disabled={sending}
          className="inline-flex items-center gap-2 px-4 h-10 border border-border hover:border-foreground/60 text-xs uppercase tracking-[0.18em] disabled:opacity-60"
          data-testid="send-test-email-btn"
        >
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          {sending ? "Sending…" : "Send test"}
        </button>
      </div>
    </section>
  );
}

export function MigrationPanel() {
  const fileRef = useRef(null);
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [lastImport, setLastImport] = useState(null);

  async function handleExport() {
    setExporting(true);
    try {
      const { data } = await api.get("/admin/migration/site-export");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 10);
      a.download = `allure-site-export-${stamp}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      const fileCount = Object.keys(data.files || {}).length;
      toast.success(`Exported ${data.services?.length || 0} services + ${fileCount} images`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Export failed");
    } finally {
      setExporting(false);
    }
  }

  async function handleImport(file) {
    if (!file) return;
    const ok = window.confirm(
      "Importing will OVERWRITE this environment's services and site settings with the contents of the JSON file. Continue?"
    );
    if (!ok) return;
    setImporting(true);
    try {
      const text = await file.text();
      const payload = JSON.parse(text);
      const { data } = await api.post("/admin/migration/site-import", payload);
      setLastImport(data);
      toast.success(`Imported ${data.services_written} services + ${data.files_imported} images`);
      setTimeout(() => window.location.reload(), 1500);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Import failed — invalid file?");
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  return (
    <section className="bg-card border border-border p-6" data-testid="migration-panel">
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
        <div className="max-w-2xl">
          <div className="eyebrow mb-2">Site migration</div>
          <h2 className="font-serif text-2xl mb-2">Move your content between preview & production</h2>
          <p className="text-sm text-muted-foreground">
            Each environment has its own database. Use <strong>Export</strong> here to download a JSON bundle of all
            your services, portfolio, and hero images. Then sign in to the other environment's Studio Manager and use
            <strong> Import</strong> to apply the same content there. Import overwrites the existing services and site settings.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={handleExport}
            disabled={exporting}
            className="inline-flex items-center gap-2 px-4 h-10 text-[10px] uppercase tracking-[0.28em] text-foreground/80 hover:text-foreground border-b border-[color:var(--gold-base)]/40 hover:border-[color:var(--gold-base)] transition-colors disabled:opacity-60"
            data-testid="migration-export-btn"
          >
            {exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <span className="text-[color:var(--gold-base)]">↗</span>}
            {exporting ? "Exporting…" : "Export site data"}
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="application/json,.json"
            className="hidden"
            onChange={(e) => handleImport(e.target.files?.[0])}
            data-testid="migration-import-input"
          />
          <button
            onClick={() => fileRef.current?.click()}
            disabled={importing}
            className="inline-flex items-center gap-2 px-4 h-10 text-[10px] uppercase tracking-[0.28em] text-foreground/80 hover:text-foreground border-b border-[color:var(--gold-base)]/40 hover:border-[color:var(--gold-base)] transition-colors disabled:opacity-60"
            data-testid="migration-import-btn"
          >
            {importing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <span className="text-[color:var(--gold-base)]">↙</span>}
            {importing ? "Importing…" : "Import site data"}
          </button>
        </div>
      </div>
      {lastImport && (
        <div className="mt-4 text-xs text-muted-foreground border-t border-border pt-3" data-testid="migration-last-result">
          Last import: {lastImport.services_written} services · {lastImport.files_imported} images · site_settings {lastImport.site_settings_written ? "applied" : "skipped"}. Reloading…
        </div>
      )}
    </section>
  );
}

/* ------------------------------------------------------------------------
   Theme picker — Lumetier per-studio personalization.

   Lets the studio owner pick from 8 curated presets and (optionally) override
   individual colors with hex inputs. Live preview by writing CSS vars onto a
   scoped wrapper element AND a small mock card. Live admin-chrome preview is
   triggered by also pushing the in-progress palette onto documentElement, then
   rolling back when the user navigates away or clicks "Reset".
   ------------------------------------------------------------------------ */

/* Brand assets — logo, cover logo, favicon.
   Each control is a small image-upload tile that mirrors the editorial split-
   row pattern used elsewhere in Site Settings (label + caption left, control
   right). We reuse POST /admin/uploads (already used for hero, portfolio,
   service images) so we don't fork the upload pipeline. */
export function BrandAssetsSection({ s, set }) {
  return (
    <Section
      title="Brand assets"
      subtitle="Your logo & favicon show on the public booking page, the client gallery viewer, and booking confirmation emails. Use PNG or SVG with a transparent background for the cleanest result."
    >
      <div className="space-y-7">
        <AssetUploader
          label="Logo"
          caption="Header logo on your public booking site & gallery — wordmark or symbol on transparent. Recommended ~600×200 PNG/SVG."
          value={s.logo_url}
          onChange={(url) => set("logo_url", url)}
          testid="logo"
          variant="wide"
        />
        <AssetUploader
          label="Cover logo (optional)"
          caption="White-on-dark variant for use over hero photographs. If empty, we use the main logo and add a soft scrim."
          value={s.cover_logo_url}
          onChange={(url) => set("cover_logo_url", url)}
          testid="cover-logo"
          variant="wide"
          darkPreview
        />
        <AssetUploader
          label="Favicon"
          caption="Browser tab icon. A square PNG / ICO / SVG at 32×32 or larger."
          value={s.favicon_url}
          onChange={(url) => set("favicon_url", url)}
          testid="favicon"
          variant="square"
        />
      </div>
    </Section>
  );
}

/* LumetierHubFeatureSection — opt-in to the public "Studios on Lumetier"
   showcase on lumetiercreatives.com. A single toggle with editorial copy
   explaining the trade ("more visibility + an SEO backlink in exchange for
   appearing in the public showcase"). When ON, the GET /studios/featured
   endpoint surfaces this studio as a card on the marketing hub. */
export function LumetierHubFeatureSection({ s, set }) {
  const featured = Boolean(s.show_in_lumetier_hub);
  return (
    <Section
      title="Featured on Lumetier"
      subtitle="Opt in to appear on the public Studios on Lumetier showcase at lumetiercreatives.com. Your studio name, location, hero photo, and a backlink to your site will be visible there."
    >
      <div className="space-y-5">
        <button
          type="button"
          onClick={() => set("show_in_lumetier_hub", !featured)}
          aria-pressed={featured}
          data-testid="branding-feature-on-lumetier-toggle"
          className={`group relative w-full text-left px-6 py-5 border transition-all ${
            featured
              ? "border-[hsl(var(--gold))] bg-[hsl(var(--gold))]/8"
              : "border-border hover:border-border/80 bg-card/40"
          }`}
        >
          <div className="flex items-start justify-between gap-5">
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-[0.32em] text-[color:var(--gold-base)]">
                Studios on Lumetier showcase
              </div>
              <div className="mt-2 font-serif text-xl italic font-light leading-tight">
                {featured ? "You're in the showcase." : "Join the showcase."}
              </div>
              <p className="mt-3 text-sm text-muted-foreground leading-relaxed max-w-lg">
                {featured
                  ? "Your studio appears on lumetiercreatives.com as a featured card with a backlink to your public site. Toggle off any time."
                  : "A small editorial card with your name, location, hero photo, and template will surface on the Lumetier marketing site. Mutually beneficial — Lumetier gains social proof, you gain a backlink + free editorial real estate on a higher-PageRank surface."}
              </p>
            </div>
            {/* Pill toggle */}
            <span
              aria-hidden
              className={`relative inline-flex shrink-0 mt-1 h-7 w-12 rounded-full border transition-colors ${
                featured ? "border-[hsl(var(--gold))] bg-[hsl(var(--gold))]" : "border-border bg-muted"
              }`}
            >
              <span
                className={`absolute top-0.5 h-6 w-6 rounded-full bg-background shadow transition-transform ${
                  featured ? "translate-x-5" : "translate-x-0.5"
                }`}
              />
            </span>
          </div>
        </button>
        <p className="text-[11px] font-serif italic text-muted-foreground/70 leading-relaxed">
          Privacy: this is opt-in. We never surface your studio without this toggle being explicitly on. Take down anytime; the card disappears from lumetiercreatives.com on your next save.
        </p>
      </div>
    </Section>
  );
}

/* LumetierCreditSection — toggle that hides the "Powered by Lumetier" footer
   credit on the studio's public site. Today the toggle is freely available
   so beta tenants can experiment; once Stripe billing is wired the section
   will gate the toggle behind a paid plan. The toggle is presented with that
   forward-looking copy already so tenants know what's coming. */
export function LumetierCreditSection({ s, set }) {
  const hidden = Boolean(s.lumetier_credit_hidden);
  return (
    <Section
      title="Lumetier credit"
      subtitle={'A small "Site by Lumetier" link sits in the footer of your public site by default — part of the network that brings new clients into the Lumetier ecosystem. You can hide it here.'}
    >
      <div className="space-y-5">
        <button
          type="button"
          onClick={() => set("lumetier_credit_hidden", !hidden)}
          aria-pressed={hidden}
          data-testid="branding-lumetier-credit-toggle"
          className={`group relative w-full text-left px-6 py-5 border transition-all ${
            hidden
              ? "border-[hsl(var(--gold))] bg-[hsl(var(--gold))]/8"
              : "border-border hover:border-border/80 bg-card/40"
          }`}
        >
          <div className="flex items-start justify-between gap-5">
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-[0.32em] text-[color:var(--gold-base)]">
                Footer credit
              </div>
              <div className="mt-2 font-serif text-xl italic font-light leading-tight">
                {hidden ? "Hidden on your public site." : "“Site by Lumetier” visible."}
              </div>
              <p className="mt-3 text-sm text-muted-foreground leading-relaxed max-w-lg">
                {hidden
                  ? "The small Lumetier credit no longer appears on your public footers. Toggle it back on to support the network anytime."
                  : "A subtle, italicized “Site by Lumetier” sits in the bottom row of every public page on your studio site. Clicking it opens lumetiercreatives.com in a new tab."}
              </p>
              <p className="mt-3 text-[10px] uppercase tracking-[0.28em] text-muted-foreground/60">
                Pro plan feature · free during beta
              </p>
            </div>
            <span
              aria-hidden
              className={`relative inline-flex shrink-0 mt-1 h-7 w-12 rounded-full border transition-colors ${
                hidden ? "border-[hsl(var(--gold))] bg-[hsl(var(--gold))]" : "border-border bg-muted"
              }`}
            >
              <span
                className={`absolute top-0.5 h-6 w-6 rounded-full bg-background shadow transition-transform ${
                  hidden ? "translate-x-5" : "translate-x-0.5"
                }`}
              />
            </span>
          </div>
        </button>
      </div>
    </Section>
  );
}

export function AssetUploader({ label, caption, value, onChange, testid, variant = "wide", darkPreview = false }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  async function pick(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/image\/(png|jpe?g|svg\+xml|webp|x-icon|vnd\.microsoft\.icon)/.test(file.type) && !file.name.match(/\.(ico|png|jpe?g|svg|webp)$/i)) {
      return toast.error("Use PNG, JPG, SVG, WebP, or ICO");
    }
    if (file.size > 6 * 1024 * 1024) return toast.error("Keep under 6 MB");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success(`${label} uploaded`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  }

  function clear() {
    if (!window.confirm(`Remove ${label}?`)) return;
    onChange("");
  }

  const tileClass = variant === "square"
    ? "w-24 h-24"
    : "w-44 h-20";
  const previewBg = darkPreview ? "bg-stone-900" : "bg-muted/40";

  return (
    <div className="grid grid-cols-1 sm:grid-cols-[180px_1fr] gap-5 items-start">
      <div>
        <div className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground font-medium mb-1">{label}</div>
        <p className="text-xs text-muted-foreground leading-relaxed">{caption}</p>
      </div>
      <div className="flex items-center gap-4">
        <div
          className={`${tileClass} ${previewBg} border border-border flex items-center justify-center overflow-hidden shrink-0`}
          data-testid={`asset-preview-${testid}`}
        >
          {value ? (
            <img src={value.startsWith("http") ? value : `${BACKEND}${value}`} alt={label} className="max-w-full max-h-full object-contain" />
          ) : (
            <ImagePlus className={`w-5 h-5 ${darkPreview ? "text-stone-600" : "text-muted-foreground/40"}`} />
          )}
        </div>
        <div className="flex flex-col gap-1.5">
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            className="inline-flex items-center gap-1.5 px-3 h-8 text-[10px] uppercase tracking-[0.22em] border border-border hover:border-foreground/60 transition-colors disabled:opacity-50"
            data-testid={`asset-upload-${testid}`}
          >
            <Upload className="w-3 h-3" /> {uploading ? "Uploading…" : value ? "Replace" : "Upload"}
          </button>
          {value && (
            <button
              type="button"
              onClick={clear}
              className="inline-flex items-center gap-1.5 px-3 h-8 text-[10px] uppercase tracking-[0.22em] text-muted-foreground hover:text-destructive transition-colors"
              data-testid={`asset-clear-${testid}`}
            >
              <X className="w-3 h-3" /> Remove
            </button>
          )}
        </div>
        <input ref={inputRef} type="file" accept="image/*,.ico" onChange={pick} className="hidden" />
      </div>
    </div>
  );
}

/* Custom domain — Cloudflare for SaaS flow.

   Status enum: none → requested → dns_verified → active.
   - requested:     CF hostname created; tenant must add CNAME at registrar.
                    Surface the exact CNAME + TXT records they need.
   - dns_verified:  CF detected the CNAME but cert isn't issued yet (~30-90s).
   - active:        LIVE — show the URL + Detach control.

   Polls the backend every 25s while in requested/dns_verified so the
   photographer watches it flip without refreshing. The "Verify DNS now"
   button triggers an immediate poll instead of waiting for the next tick. */
/* Live Links — Site Settings header panel that surfaces the actual public
   URLs of the tenant's site so they can preview / share without leaving the
   admin. Shows the studio's `/s/<slug>` URLs by default; when a custom domain
   is active, surfaces both alongside so the studio can copy whichever they
   want to share (their branded vanity URL OR the canonical platform URL).

   We surface TWO links: the studio's main public site (where their hero,
   portfolio, and story live) and the booking flow (the deep link to send
   straight to "select a session"). The main link is the canonical share-
   anywhere URL; the booking link is the bio-link / email-signature shortcut. */
export function LiveLinksPanel({ s }) {
  // Tenant slug used in /s/<slug>/* routing. The site_settings doc stores it
  // under different keys depending on how the studio was seeded — fall back
  // through them so the panel always shows the right URL. Final fallback is
  // a placeholder so the panel still renders meaningfully on brand-new sites.
  const slug = s.slug || s.studio_slug || s.studio_id || "your-studio";
  // Build absolute URLs so the copied value is shareable as-is. window.origin
  // works for both the lumetiercreatives.com production host and the preview
  // *.preview.emergentagent.com host without any config.
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const baseSlugUrl = `${origin}/s/${slug}`;

  // Custom-domain URL (only when CF status === active). We always anchor to
  // https:// because Cloudflare for SaaS provisions TLS automatically before
  // status flips to active.
  const cdActive = s.custom_domain_status === "active" && s.custom_domain;
  const cdBase = cdActive ? `https://${s.custom_domain}` : null;

  // The two links a studio actually pastes places:
  //  · "Main" — the studio's hero/portfolio/story landing (the URL they put
  //    on a business card, in an email signature, in a press kit).
  //  · "Booking" — deep link that drops visitors straight onto the booking
  //    flow with no intermediate clicks (best for Instagram bio / DM auto-
  //    reply / contact-form follow-ups).
  const links = [
    { id: "main",    label: "Main site", path: "",         hint: "Hero, portfolio, and your story." },
    { id: "booking", label: "Booking",   path: "/booking", hint: "Drops visitors straight onto the booking flow." },
  ];

  return (
    <section className="bg-card border border-border p-8 md:p-10" data-testid="live-links-panel">
      <div className="flex items-baseline justify-between gap-4 mb-6 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-2 text-[10px] uppercase tracking-[0.32em] text-muted-foreground font-sans">
            <Globe className="w-3 h-3 text-[#C9A961]" />
            <span>Public URLs</span>
          </div>
          <h2 className="font-serif text-2xl md:text-3xl tracking-tight leading-tight text-foreground">
            Share these links.
          </h2>
        </div>
        {cdActive && (
          <span className="inline-flex items-center gap-1.5 px-2 py-1 text-[9px] uppercase tracking-[0.22em] bg-emerald-100 text-emerald-800 border border-emerald-200">
            <CheckCircle2 className="w-3 h-3" /> Custom domain live
          </span>
        )}
      </div>

      <div className="space-y-7">
        {links.map((link) => (
          <LiveLinkRow
            key={link.id}
            link={link}
            baseSlugUrl={baseSlugUrl}
            cdBase={cdBase}
          />
        ))}
      </div>

      <p className="text-[11px] text-muted-foreground/70 mt-6 italic font-serif">
        Use the main link in your email signature and press kit. Paste the booking link into your Instagram bio and contact-form auto-reply.
      </p>
    </section>
  );
}

export function LiveLinkRow({ link, baseSlugUrl, cdBase }) {
  const slugUrl = `${baseSlugUrl}${link.path}`;
  const cdUrl = cdBase ? `${cdBase}${link.path}` : null;
  return (
    <div className="space-y-2" data-testid={`live-link-${link.id}`}>
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <div className="text-[10px] uppercase tracking-[0.32em] text-[color:var(--gold-base)] font-sans">
          {link.label}
        </div>
        <a
          href={slugUrl}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.22em] text-muted-foreground hover:text-foreground transition-colors"
          data-testid={`live-link-open-${link.id}`}
        >
          Open <ExternalLink className="w-3 h-3" />
        </a>
      </div>
      <CopyField label={link.label} value={slugUrl} testid={`live-link-slug-${link.id}`} />
      {cdUrl && (
        <CopyField label={`${link.label} (custom domain)`} value={cdUrl} testid={`live-link-cd-${link.id}`} />
      )}
      {link.hint && (
        <p className="text-[11px] text-muted-foreground/70 italic font-serif pt-1">
          {link.hint}
        </p>
      )}
    </div>
  );
}

export function CustomDomainSection({ s, setS }) {
  const [state, setState] = useState({
    domain: s.custom_domain_requested || "",
    status: s.custom_domain_status || "none",
    cname_target: "customers.lumetiercreatives.com",
    cloudflare_configured: true,
    ownership_verification: {},
    cloudflare_hostname_status: "",
    cloudflare_ssl_status: "",
    last_polled_at: null,
  });
  const [draft, setDraft] = useState(s.custom_domain_requested || "");
  const [submitting, setSubmitting] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [detaching, setDetaching] = useState(false);

  // Pull live state on mount so we have the CNAME target + ownership records.
  useEffect(() => {
    let alive = true;
    api.get("/admin/site/custom-domain").then(({ data }) => {
      if (!alive) return;
      setState(data);
      setDraft(data.domain || "");
    }).catch(() => { /* leave defaults */ });
    return () => { alive = false; };
  }, []);

  // Poll while in transitional states.
  useEffect(() => {
    if (state.status !== "requested" && state.status !== "dns_verified") return;
    const t = setInterval(() => {
      api.get("/admin/site/custom-domain").then(({ data }) => setState(data)).catch(() => {});
    }, 25_000);
    return () => clearInterval(t);
  }, [state.status]);

  async function submit() {
    const domain = draft.trim().toLowerCase().replace(/^https?:\/\//, "").replace(/\/$/, "");
    if (!domain) return toast.error("Enter the domain you'd like to use");
    if (domain === state.domain) return toast("That domain is already submitted");
    setSubmitting(true);
    try {
      const { data } = await api.post("/admin/site/custom-domain", { domain });
      const fresh = await api.get("/admin/site/custom-domain");
      setState(fresh.data);
      setS?.((prev) => ({
        ...prev,
        custom_domain_requested: data.domain,
        custom_domain_status: "requested",
      }));
      toast.success("Domain registered with Cloudflare. Add the DNS records below to go live.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't submit");
    } finally {
      setSubmitting(false);
    }
  }

  async function verifyNow() {
    setRefreshing(true);
    try {
      const { data } = await api.post("/admin/site/custom-domain/refresh");
      // Re-fetch full state so we get ownership_verification etc.
      const fresh = await api.get("/admin/site/custom-domain");
      setState(fresh.data);
      if (data.status === "active") {
        toast.success("Your domain is LIVE 🎉");
      } else if (data.status === "dns_verified") {
        toast.success("DNS verified — issuing SSL certificate (usually 30–90s)");
      } else {
        toast("Still pending — DNS may take a few minutes to propagate. Try again in 2-3 min.");
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Verification failed");
    } finally {
      setRefreshing(false);
    }
  }

  async function detach() {
    if (!window.confirm(`Detach ${state.domain}? You'll lose the SSL certificate and routing.`)) return;
    setDetaching(true);
    try {
      await api.delete("/admin/site/custom-domain");
      setState((prev) => ({ ...prev, domain: "", status: "none", ownership_verification: {} }));
      setDraft("");
      setS?.((prev) => ({ ...prev, custom_domain_requested: "", custom_domain_status: "none" }));
      toast.success("Custom domain detached.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't detach");
    } finally {
      setDetaching(false);
    }
  }

  const ov = state.ownership_verification || {};
  const hasDnsInstructions = (state.status === "requested" || state.status === "dns_verified") && state.domain;

  return (
    <Section
      title="Custom domain"
      subtitle={
        <>
          Want your gallery and booking site to live at{" "}
          <code className="font-mono text-foreground">www.your-studio.com</code> instead of{" "}
          <code className="font-mono text-foreground">lumetiercreatives.com/s/{s.slug || "your-studio"}</code>?
          Add your domain, point one DNS record at us, and we&apos;ll issue an SSL certificate automatically — usually within 2 minutes.
        </>
      }
    >
      {/* === ACTIVE === */}
      {state.status === "active" && state.domain && (
        <div className="border border-emerald-500/40 bg-emerald-500/10 px-4 py-4 mb-5" data-testid="custom-domain-active">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="w-5 h-5 text-emerald-500 mt-0.5 shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-emerald-900 mb-1">Your domain is live</div>
              <a
                href={`https://${state.domain}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-sm text-emerald-900/80 underline break-all"
              >
                https://{state.domain}
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
            </div>
            <button
              type="button"
              onClick={detach}
              disabled={detaching}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] uppercase tracking-[0.22em] border border-emerald-700/40 text-emerald-900 hover:bg-emerald-700/10 disabled:opacity-40 shrink-0"
              data-testid="custom-domain-detach"
            >
              <Unlink className="w-3 h-3" />
              {detaching ? "Detaching…" : "Detach"}
            </button>
          </div>
        </div>
      )}

      {/* === DNS VERIFIED, WAITING ON SSL === */}
      {state.status === "dns_verified" && state.domain && (
        <div className="border border-sky-500/40 bg-sky-500/10 px-4 py-3 mb-5 flex items-start gap-3" data-testid="custom-domain-dns-verified">
          <Loader2 className="w-4 h-4 text-sky-500 mt-0.5 shrink-0 animate-spin" />
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium text-sky-900">DNS verified — issuing SSL certificate</div>
            <div className="text-xs text-sky-900/80 mt-0.5">
              Cloudflare detected your CNAME record for <strong>{state.domain}</strong>. The SSL cert usually finishes in 30–90 seconds.
            </div>
          </div>
        </div>
      )}

      {/* === DNS SETUP CARDS (shown while requested) === */}
      {hasDnsInstructions && (
        <div className="border border-amber-500/40 bg-amber-500/[0.06] px-5 py-5 mb-5 space-y-5" data-testid="custom-domain-setup">
          <div className="flex items-start gap-3">
            <Globe className="w-5 h-5 text-amber-500 mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-foreground mb-0.5">
                Two DNS records to add at your registrar
              </div>
              <div className="text-xs text-muted-foreground">
                Once both records propagate, we&apos;ll auto-issue an SSL cert and your domain goes live.
                Add these in your registrar&apos;s DNS panel (GoDaddy / Namecheap / Google Domains / Cloudflare / etc.).
              </div>
            </div>
          </div>

          {/* Record 1 — CNAME (routing) */}
          <div className="pl-8 space-y-2">
            <div className="flex items-center gap-2">
              <span className="text-[10px] uppercase tracking-[0.22em] font-medium text-amber-600">
                Record 1 · CNAME
              </span>
              <span className="text-[10px] text-muted-foreground">(points traffic to us)</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-[120px_1fr] gap-2 items-center">
              <span className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground">Type</span>
              <div className="text-sm font-mono text-foreground">CNAME</div>
              <span className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground">Name / Host</span>
              <CopyField label="Host" value={state.domain} testid="cname-name" />
              <span className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground">Target / Value</span>
              <CopyField label="Target" value={state.cname_target} testid="cname-target" />
            </div>
          </div>

          {/* Record 2 — TXT (ownership verification) */}
          {ov.name && ov.value && (
            <div className="pl-8 space-y-2 pt-2 border-t border-amber-500/20">
              <div className="flex items-center gap-2">
                <span className="text-[10px] uppercase tracking-[0.22em] font-medium text-amber-600">
                  Record 2 · TXT
                </span>
                <span className="text-[10px] text-muted-foreground">(proves you own the domain)</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-[120px_1fr] gap-2 items-center">
                <span className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground">Type</span>
                <div className="text-sm font-mono text-foreground">TXT</div>
                <span className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground">Name / Host</span>
                <CopyField label="TXT Host" value={ov.name} testid="txt-name" />
                <span className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground">Value</span>
                <CopyField label="TXT Value" value={ov.value} testid="txt-value" />
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="pl-8 pt-2 flex items-center gap-3 flex-wrap">
            <button
              type="button"
              onClick={verifyNow}
              disabled={refreshing}
              className="inline-flex items-center gap-2 px-4 py-2 text-[11px] uppercase tracking-[0.22em] bg-foreground text-background hover:opacity-90 disabled:opacity-40 transition-opacity"
              data-testid="custom-domain-verify"
            >
              {refreshing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
              {refreshing ? "Checking…" : "Verify DNS now"}
            </button>
            <button
              type="button"
              onClick={detach}
              disabled={detaching}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-[10px] uppercase tracking-[0.22em] border border-border text-muted-foreground hover:bg-muted/30 disabled:opacity-40"
              data-testid="custom-domain-cancel"
            >
              <X className="w-3 h-3" />
              Cancel & remove
            </button>
            <span className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground ml-auto">
              We auto-recheck every 5 min · status: {state.cloudflare_hostname_status || "pending"}
              {state.cloudflare_ssl_status && ` · ssl: ${state.cloudflare_ssl_status}`}
            </span>
          </div>

          {/* Registrar-specific quick guides */}
          <details className="pl-8 pt-2 border-t border-amber-500/20">
            <summary className="text-[11px] uppercase tracking-[0.22em] text-amber-700 cursor-pointer hover:text-amber-900">
              Need help finding your DNS panel?
            </summary>
            <div className="mt-3 space-y-3 text-xs text-muted-foreground leading-relaxed">
              <div>
                <strong className="text-foreground">GoDaddy</strong>: Sign in → My Products → DNS next to your domain → Add new record.
              </div>
              <div>
                <strong className="text-foreground">Namecheap</strong>: Domain List → Manage → Advanced DNS tab → Add new record.
              </div>
              <div>
                <strong className="text-foreground">Google Domains / Squarespace</strong>: My domains → DNS → Custom records → Add.
              </div>
              <div>
                <strong className="text-foreground">Cloudflare</strong>: DNS → Records → Add record. <em>Important: set Proxy status to DNS only (grey cloud) — not proxied.</em>
              </div>
            </div>
          </details>
        </div>
      )}

      {/* === SUBMIT FORM === */}
      {state.status !== "active" && (
        <label className="block">
          <span className="block text-[11px] uppercase tracking-[0.22em] text-muted-foreground font-medium mb-1.5">
            {hasDnsInstructions ? "Change domain" : "Your domain"}
          </span>
          <div className="flex items-stretch gap-2">
            <div className="inline-flex items-center px-3 bg-muted/40 text-muted-foreground text-sm border border-r-0 border-border">
              https://
            </div>
            <input
              type="text"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="www.your-studio.com"
              className="flex-1 bg-background border border-border px-3 py-2 text-sm focus:outline-none focus:border-foreground/60 transition-colors"
              data-testid="custom-domain-input"
            />
            <button
              type="button"
              onClick={submit}
              disabled={submitting || !draft.trim() || draft.trim() === state.domain}
              className="inline-flex items-center gap-2 px-5 text-[11px] uppercase tracking-[0.22em] bg-foreground text-background hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="custom-domain-submit"
            >
              {submitting ? "Submitting…" : hasDnsInstructions ? "Update" : "Add domain"}
            </button>
          </div>
        </label>
      )}

      {state.status === "none" && (
        <p className="text-xs text-muted-foreground mt-3">
          Powered by Cloudflare for SaaS · automatic SSL via Let&apos;s Encrypt · no extra charge during the Charter period.
        </p>
      )}
    </Section>
  );
}

/* Signature theme pairings — what each template was DESIGNED to render with.
   Clicking "Use matching theme" on a card applies BOTH typeface_pair and the
   accent override in one motion so the tenant lands on a cohesive, designer-
   approved aesthetic without piecemeal tweaking. Tenants can still customize
   from there; this is just the "make it feel finished" shortcut. */

export function TemplateSection({ s, set }) {
  const current = s.template_id || "editorial";
  const [browseOpen, setBrowseOpen] = useState(false);
  // The tenant slug powers the "Preview live →" deep-link on each card.
  // Falls back to "your-studio" only so we don't construct a broken URL
  // before settings have loaded; the disabled fallback never actually
  // ships to a real card render.
  const slug = s.slug || s.studio_slug || s.studio_id || "your-studio";

  /* Picking a template is the SAME ACTION as picking its signature look —
     a user who selects "Atelier" expects the rose-gold blush canvas + Playfair
     italic, not Atelier's chrome with Editorial's dark canvas. Without this,
     the v2 templates (Atelier / Heirloom / Vista / Brutalist / Botanical /
     Spotlight) all reuse Editorial's component bundle for their inner pages,
     so picking just `template_id` left them looking ~90% identical to
     Editorial — exactly the bug reported on 2026-06-28.

     Applies in one shot: template_id + matching typeface + matching theme
     palette + matching accent hex. Recipes without a `theme_id` (editorial,
     minimal, bold, journal) preserve whatever theme the studio already had —
     those four templates were designed to host any theme. The save bar
     gives the studio a clean undo path until they hit Save. */
  function pickTemplate(templateId) {
    const recipe = SUGGESTED_THEMES[templateId];
    set("template_id", templateId);
    if (recipe) {
      set("typeface_pair", recipe.typeface_pair);
      if (recipe.theme_id) {
        set("theme_id", recipe.theme_id);
      }
      set("theme_overrides", { ...(s.theme_overrides || {}), primary: recipe.accent_hex });
      toast.success(`Switched to ${TEMPLATES[templateId]?.label} · ${recipe.accent_label}`);
    } else {
      toast.success(`Switched to ${TEMPLATES[templateId]?.label}`);
    }
    // Collapse the browse grid back into the "Currently active" hero card
    // so the section reads calmly again after a pick. The save bar makes
    // the change visible and gives an undo path, so we don't need to keep
    // the grid open to confirm.
    setBrowseOpen(false);
  }

  /* Re-apply the matching recipe explicitly — used by the "Restore signature
     look" CTA on each card. Same body as pickTemplate, kept separate so we
     can toast with different copy when it's a re-apply vs. first pick. */
  function applyMatchingTheme(templateId) {
    const recipe = SUGGESTED_THEMES[templateId];
    if (!recipe) return;
    set("template_id", templateId);
    set("typeface_pair", recipe.typeface_pair);
    if (recipe.theme_id) {
      set("theme_id", recipe.theme_id);
    }
    set("theme_overrides", { ...(s.theme_overrides || {}), primary: recipe.accent_hex });
    toast.success(`Restored the ${TEMPLATES[templateId]?.label} signature look · ${recipe.accent_label}`);
    setBrowseOpen(false);
  }

  const activeTemplate = TEMPLATES[current] || TEMPLATES.editorial;
  const activeRecipe = SUGGESTED_THEMES[current];
  const otherCount = Object.values(TEMPLATES).filter((t) => t.id !== current).length;

  return (
    <section className="bg-card border border-border p-8 md:p-12" data-testid="template-section">
      <div className="mb-8 pb-8 border-b border-border max-w-2xl">
        <div className="flex items-center gap-2 mb-3 text-[10px] uppercase tracking-[0.32em] text-muted-foreground font-sans">
          <Sparkles className="w-3 h-3 text-[#C9A961]" />
          <span>Template</span>
        </div>
        <h2 className="font-serif text-3xl md:text-4xl tracking-tight leading-tight text-foreground">
          Site template
        </h2>
        <p className="text-sm text-muted-foreground mt-3 leading-relaxed">
          The structural layout that powers your public booking site. Picking a template
          also applies its signature palette and typeface — pick the look and feel in
          one click. You can still fine-tune theme &amp; colors below afterwards.
        </p>
      </div>

      {/* ── Current-template hero (compact, always visible) ──────────────
          Shows just the active template so the section reads calmly. The
          full 10-card grid is one click away via the "Browse templates"
          CTA below — Notion / Linear "the choice you've made is the
          focus, not a wall of alternatives" pattern. */}
      <div
        className="relative p-6 md:p-7 border border-border bg-muted/20 overflow-hidden"
        data-testid="template-current-card"
      >
        <div
          className="absolute top-0 left-0 right-0 h-1"
          style={{ background: activeTemplate.previewColor }}
        />
        <div className="flex flex-col md:flex-row md:items-end gap-6 mt-2">
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.32em] text-muted-foreground mb-2">
              Currently active
            </div>
            <div className="flex items-baseline gap-3 mb-2">
              <div className="font-serif text-3xl md:text-4xl tracking-tight text-foreground" data-testid={`template-active-${activeTemplate.id}`}>
                {activeTemplate.label}
              </div>
              <span className="inline-flex items-center justify-center w-6 h-6 bg-foreground text-background shrink-0">
                <Check className="w-3.5 h-3.5" />
              </span>
            </div>
            <div className="text-[10px] uppercase tracking-[0.28em] text-[#C9A961] mb-2 font-sans">
              {activeTemplate.tagline}
            </div>
            {activeTemplate.niche && (
              <div className="text-[9px] uppercase tracking-[0.32em] text-muted-foreground/70 mb-3 font-sans">
                {activeTemplate.niche}
              </div>
            )}
            <p className="text-xs text-muted-foreground leading-relaxed max-w-xl">
              {activeTemplate.description}
            </p>
          </div>
          <div className="flex flex-col items-stretch md:items-end gap-3 shrink-0">
            {activeRecipe && (
              <div className="flex items-center gap-2 md:justify-end">
                <span
                  className="w-3 h-3 rounded-full border border-border/40 shrink-0"
                  style={{ background: activeRecipe.accent_hex }}
                  aria-hidden="true"
                />
                <span className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                  {activeRecipe.accent_label}
                </span>
              </div>
            )}
            <button
              type="button"
              onClick={() => setBrowseOpen((o) => !o)}
              aria-expanded={browseOpen}
              data-testid="template-browse-toggle"
              className="inline-flex items-center justify-between gap-3 px-5 h-11 border border-[color:var(--gold-base)]/60 text-[10px] uppercase tracking-[0.32em] text-[color:var(--gold-base)] hover:bg-[color:var(--gold-base)]/10 transition-colors"
            >
              <span>{browseOpen ? "Hide other templates" : `Browse ${otherCount} other templates`}</span>
              <ChevronDown className={`w-3.5 h-3.5 transition-transform ${browseOpen ? "rotate-180" : ""}`} />
            </button>
            <a
              href={`/s/${slug}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="template-view-live-site"
              className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-2 md:justify-end"
            >
              <span>View your live site</span>
              <span aria-hidden>↗</span>
            </a>
          </div>
        </div>
      </div>

      {/* ── SAVED LOOKS — bookmarked template + theme + typeface combos ──
          Built for seasonal-rotation workflows: "Wedding season 2026",
          "Brand campaign — winter", etc. Capture the current draft as
          a named look, apply later in one click. Plumbed via
          site_settings.saved_looks (List[Dict]) — same array-replace
          pattern as custom_palettes, so no new endpoints. */}
      <SavedLooksPanel s={s} set={set} pickTemplate={pickTemplate} />

      {/* ── Expandable grid (closed by default) ────────────────────────── */}
      {browseOpen && (
      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="template-grid">
        {Object.values(TEMPLATES).map((t) => {
          const active = t.id === current;
          const disabled = t.status !== "ready";
          const recipe = SUGGESTED_THEMES[t.id];
          return (
            <div
              key={t.id}
              role="button"
              tabIndex={disabled ? -1 : 0}
              aria-disabled={disabled}
              aria-pressed={active}
              onClick={() => !disabled && pickTemplate(t.id)}
              onKeyDown={(e) => {
                if (disabled) return;
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  pickTemplate(t.id);
                }
              }}
              data-testid={`template-card-${t.id}`}
              className={`relative text-left p-6 border transition-all overflow-hidden group focus:outline-none focus-visible:ring-2 focus-visible:ring-foreground/40 ${
                active
                  ? "border-foreground bg-muted/40 ring-1 ring-foreground/20"
                  : disabled
                  ? "border-border opacity-50 cursor-not-allowed"
                  : "border-border hover:border-foreground/60 hover:bg-muted/20 cursor-pointer"
              }`}
            >
              {/* Color stripe — preview accent */}
              <div
                className="absolute top-0 left-0 right-0 h-1"
                style={{ background: t.previewColor }}
              />
              <div className="flex items-baseline justify-between gap-3 mb-2 mt-2">
                <div className="font-serif text-2xl md:text-3xl tracking-tight text-foreground">
                  {t.label}
                </div>
                {disabled && (
                  <span className="text-[9px] uppercase tracking-[0.28em] text-muted-foreground border border-border px-2 py-0.5">
                    Coming soon
                  </span>
                )}
                {active && !disabled && (
                  <span
                    className="inline-flex items-center justify-center w-6 h-6 bg-foreground text-background shrink-0"
                    data-testid={`template-active-${t.id}`}
                  >
                    <Check className="w-3.5 h-3.5" />
                  </span>
                )}
              </div>
              <div className="text-[10px] uppercase tracking-[0.28em] text-[#C9A961] mb-3 font-sans">
                {t.tagline}
              </div>
              {t.niche && (
                <div className="text-[9px] uppercase tracking-[0.32em] text-muted-foreground/70 mb-3 font-sans">
                  {t.niche}
                </div>
              )}
              <p className="text-xs text-muted-foreground leading-relaxed">
                {t.description}
              </p>

              {/* "Use matching theme" CTA — applies signature typeface + accent
                  in one shot. Stops propagation so it doesn't double-fire the
                  card-level template pick. */}
              {!disabled && recipe && (
                <div className="mt-4 pt-4 border-t border-border/50 flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2 min-w-0">
                    <span
                      className="w-3 h-3 rounded-full border border-border/40 shrink-0"
                      style={{ background: recipe.accent_hex }}
                      aria-hidden="true"
                    />
                    <span className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground truncate">
                      {recipe.accent_label} · {TYPEFACE_PAIRS.find((p) => p.id === recipe.typeface_pair)?.serifLabel || recipe.typeface_pair}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); applyMatchingTheme(t.id); }}
                    data-testid={`suggest-theme-${t.id}`}
                    className="text-[10px] uppercase tracking-[0.22em] text-[color:var(--gold-base)] hover:text-[color:var(--gold-deep)] border-b border-[color:var(--gold-base)]/40 hover:border-[color:var(--gold-deep)] pb-0.5 shrink-0"
                  >
                    Restore signature look →
                  </button>
                </div>
              )}

              {/* "Preview live →" — opens the FULL public site in a new tab,
                  hot-swapped to this template via ?preview_template=<id>.
                  The server-side template_id is NOT touched; closing the tab
                  exits the preview cleanly. Lets the studio audition every
                  template against their real content in seconds without
                  committing or risking their saved settings. */}
              {!disabled && (
                <div className="mt-3 pt-3 border-t border-dashed border-border/40">
                  <a
                    href={`/s/${slug}?preview_template=${t.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    data-testid={`template-preview-live-${t.id}`}
                    className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <span>Preview on your live site (new tab)</span>
                    <span aria-hidden>↗</span>
                  </a>
                </div>
              )}
            </div>
          );
        })}
      </div>
      )}
    </section>
  );
}

/* ── SavedLooksPanel ─────────────────────────────────────────────────────
   Bookmarks a full template + theme + typeface + accent combination as a
   named "look". Rendered inside TemplateSection so the capture button
   sits next to the active-template hero card it captures FROM.

   Storage: site_settings.saved_looks is a List[Dict] mirroring the
   custom_palettes pattern. Each look = {
     id, name, template_id, theme_id, typeface_pair,
     accent_hex, theme_overrides, created_at: iso }.

   Capture = snapshot of the current draft. Apply = `set()` each field on
   the parent draft so the save bar still owns commit. Delete = filter
   out by id; auto-saves the new array via the same parent `set`. */
function SavedLooksPanel({ s, set, pickTemplate }) {
  const looks = Array.isArray(s.saved_looks) ? s.saved_looks : [];
  const [naming, setNaming] = useState(false);
  const [name, setName] = useState("");

  const currentTemplate = TEMPLATES[s.template_id || "editorial"];
  const currentThemeLabel = SUGGESTED_THEMES[s.template_id]?.theme_id || s.theme_id || "editorial";

  function captureLook() {
    const trimmed = name.trim();
    if (!trimmed) {
      toast.error("Give the look a name first.");
      return;
    }
    const look = {
      id: `look-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      name: trimmed,
      template_id: s.template_id || "editorial",
      theme_id: s.theme_id || "editorial",
      typeface_pair: s.typeface_pair || "cormorant-manrope",
      accent_hex: s.theme_overrides?.primary || "",
      theme_overrides: s.theme_overrides || {},
      created_at: new Date().toISOString(),
    };
    const next = [...looks, look];
    set("saved_looks", next);
    setName("");
    setNaming(false);
    toast.success(`Saved “${trimmed}”. Apply it any time from this panel.`);
  }

  function applyLook(look) {
    // Apply via the same `set()` API used everywhere — keeps the save bar
    // semantics consistent. Order matches pickTemplate so the matching
    // theme cleanup feels identical.
    set("template_id", look.template_id);
    if (look.theme_id) set("theme_id", look.theme_id);
    if (look.typeface_pair) set("typeface_pair", look.typeface_pair);
    set("theme_overrides", look.theme_overrides || (look.accent_hex ? { primary: look.accent_hex } : {}));
    toast.success(`Restored “${look.name}”.`);
  }

  function deleteLook(id) {
    set("saved_looks", looks.filter((l) => l.id !== id));
    toast("Look removed.");
  }

  return (
    <div className="mt-6 p-5 md:p-6 border border-dashed border-border bg-muted/10" data-testid="saved-looks-panel">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-4">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.32em] text-[color:var(--gold-base)] mb-1">
            Saved looks
          </div>
          <p className="text-xs text-muted-foreground leading-relaxed sm:max-w-md">
            Bookmark this template + theme + typeface combo so you can swap
            between seasonal looks (&ldquo;Wedding 2026&rdquo;, &ldquo;Brand winter&rdquo;) with one
            click — without re-picking pieces every time.
          </p>
        </div>
        {!naming && (
          <button
            type="button"
            onClick={() => setNaming(true)}
            data-testid="saved-look-capture-btn"
            className="w-full sm:w-auto shrink-0 inline-flex items-center justify-center gap-2 px-4 h-10 text-[10px] uppercase tracking-[0.32em] border border-[color:var(--gold-base)]/60 text-[color:var(--gold-base)] hover:bg-[color:var(--gold-base)]/10 transition-colors"
          >
            <Plus className="w-3.5 h-3.5" />
            Capture current look
          </button>
        )}
      </div>

      {naming && (
        <div className="mb-5 p-4 border border-border bg-background flex flex-wrap items-end gap-3" data-testid="saved-look-name-form">
          <div className="flex-1 min-w-[220px]">
            <label className="block text-[10px] uppercase tracking-[0.32em] text-muted-foreground mb-2">
              Name this look
            </label>
            <input
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") captureLook(); if (e.key === "Escape") { setName(""); setNaming(false); } }}
              placeholder={`e.g. ${currentTemplate?.label || "Editorial"} · Winter`}
              data-testid="saved-look-name-input"
              className="input w-full"
            />
            <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground/70 mt-2">
              Captures: <strong className="text-foreground">{currentTemplate?.label || "Editorial"}</strong>
              {" · "}
              <span style={{ color: "var(--gold-base)" }}>{currentThemeLabel}</span>
              {s.theme_overrides?.primary && (
                <> · <span style={{ color: s.theme_overrides.primary }}>●</span> {s.theme_overrides.primary}</>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={captureLook}
              data-testid="saved-look-save-btn"
              className="px-5 h-10 text-[10px] uppercase tracking-[0.32em] bg-foreground text-background hover:opacity-90 transition-opacity"
            >
              Save look
            </button>
            <button
              type="button"
              onClick={() => { setName(""); setNaming(false); }}
              className="px-4 h-10 text-[10px] uppercase tracking-[0.32em] border border-border text-muted-foreground hover:text-foreground transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {looks.length === 0 && !naming && (
        <div className="text-xs text-muted-foreground/70 italic">
          No saved looks yet. Capture the one you’re wearing now and we’ll keep it for you.
        </div>
      )}

      {looks.length > 0 && (
        <ul className="flex flex-wrap gap-2" data-testid="saved-look-list">
          {looks.map((look) => {
            const lookTemplate = TEMPLATES[look.template_id];
            return (
              <li key={look.id}
                  className="group inline-flex items-center gap-2 pl-3 pr-1 py-1 border border-border bg-background hover:border-[color:var(--gold-base)]/60 transition-colors">
                <span
                  className="w-2.5 h-2.5 rounded-full border border-border/40 shrink-0"
                  style={{ background: look.accent_hex || "transparent" }}
                  aria-hidden
                />
                <button
                  type="button"
                  onClick={() => applyLook(look)}
                  data-testid={`saved-look-apply-${look.id}`}
                  className="text-xs text-foreground hover:text-[color:var(--gold-base)] transition-colors"
                  title={`Apply look: ${look.name}`}
                >
                  {look.name}
                  <span className="ml-2 text-[9px] uppercase tracking-[0.28em] text-muted-foreground">
                    {lookTemplate?.label || look.template_id}
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => deleteLook(look.id)}
                  data-testid={`saved-look-delete-${look.id}`}
                  className="ml-1 w-6 h-6 inline-flex items-center justify-center text-muted-foreground hover:text-destructive opacity-60 hover:opacity-100 transition-all"
                  title={`Delete ${look.name}`}
                  aria-label={`Delete saved look ${look.name}`}
                >
                  <X className="w-3 h-3" />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export function ThemeSection({ s, set }) {
  const currentThemeId = s.theme_id || DEFAULT_THEME_ID;
  const currentOverrides = s.theme_overrides || {};
  const resolved = resolvePalette(currentThemeId, currentOverrides);
  const hasOverrides = Object.keys(currentOverrides).filter((k) => currentOverrides[k]).length > 0;
  const currentTheme = getThemeById(currentThemeId);
  // Whether the admin chrome is in dark mode. We only push the in-progress
  // tenant palette onto <html> as inline CSS vars when the studio owner is
  // currently viewing the admin in DARK mode — pushing in light mode would
  // beat both the `:root` cream defaults and the `.admin-dark` class rule
  // and pin the chrome to the tenant's dark canvas regardless of the toggle.
  const darkChrome = useAdminChromeMode();

  const [pickerOpen, setPickerOpen] = useState(false);
  const pickerRef = useRef(null);

  // Close the dropdown on outside-click / Escape so it feels native.
  useEffect(() => {
    if (!pickerOpen) return;
    function onDocClick(e) {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) setPickerOpen(false);
    }
    function onKey(e) {
      if (e.key === "Escape") setPickerOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [pickerOpen]);

  // Live-preview the in-progress palette on the documentElement so studio
  // owners can SEE the Studio Manager chrome shift while they're picking. Rolls
  // back when the panel unmounts (route change). The PATCH /admin/site save
  // still persists the final value separately. Skipped when the admin chrome
  // is in light mode (see darkChrome note above).
  const overridesKey = JSON.stringify(currentOverrides);
  useEffect(() => {
    if (!darkChrome) return;
    const cleanup = applyTheme({ themeId: currentThemeId, overrides: JSON.parse(overridesKey) });
    return cleanup;
  }, [currentThemeId, overridesKey, darkChrome]);

  function pickTheme(id) {
    set("theme_id", id);
    // When switching presets, clear in-flight overrides so the new preset shows
    // in its pure form. Owner can re-customize after picking.
    set("theme_overrides", {});
    setPickerOpen(false);
  }

  function setOverride(key, hex) {
    const next = { ...currentOverrides };
    if (!hex || hex === getThemeById(currentThemeId).palette[key]) {
      delete next[key];
    } else {
      next[key] = hex;
    }
    set("theme_overrides", next);
  }

  function resetOverrides() {
    set("theme_overrides", {});
    toast("Reverted to preset colors");
  }

  // ── Custom (user-saved) palettes ─────────────────────────────────────
  const customPalettes = Array.isArray(s.custom_palettes) ? s.custom_palettes : [];
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [paletteName, setPaletteName] = useState("");
  const [savingPalette, setSavingPalette] = useState(false);

  // Persist the custom_palettes array immediately via PATCH /admin/site so the
  // photographer doesn't lose their saved palettes if they forget to hit the
  // top-level Save changes button. Updates parent state on success.
  async function persistCustomPalettes(nextList, successMsg) {
    setSavingPalette(true);
    try {
      const { data } = await api.patch("/admin/site", { custom_palettes: nextList });
      // Mirror the server response into parent state so the rest of the page
      // (Save button dirty-check, etc.) sees the latest value.
      set("custom_palettes", data.custom_palettes || nextList);
      if (successMsg) toast.success(successMsg);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save palette");
    } finally {
      setSavingPalette(false);
    }
  }

  function openSaveDialog() {
    // Pre-fill with a tasteful default so the dialog isn't empty.
    setPaletteName(hasOverrides ? `${currentTheme.name} · custom` : `${currentTheme.name} fork`);
    setSaveDialogOpen(true);
  }

  async function confirmSavePalette() {
    const name = paletteName.trim();
    if (!name) {
      toast.error("Give your palette a name");
      return;
    }
    // Snapshot the FULL resolved 6-key palette so the saved entry is
    // self-contained and won't drift if the underlying preset changes.
    const snapshot = {
      id: `cp_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`,
      name,
      based_on: currentThemeId,
      palette: {
        background: resolved.background,
        foreground: resolved.foreground,
        primary: resolved.primary,
        primary_foreground: resolved.primary_foreground,
        accent: resolved.accent,
        muted: resolved.muted,
      },
      created_at: new Date().toISOString(),
    };
    const next = [...customPalettes, snapshot];
    await persistCustomPalettes(next, `Saved "${name}" to your palettes`);
    setSaveDialogOpen(false);
    setPaletteName("");
  }

  function pickCustomPalette(cp) {
    // Apply the saved snapshot as a full set of overrides on top of the
    // palette's original preset base. Every key is overridden so the live
    // result == the saved snapshot regardless of what the preset evolves into.
    set("theme_id", cp.based_on || DEFAULT_THEME_ID);
    set("theme_overrides", { ...cp.palette });
    setPickerOpen(false);
    toast(`Applied "${cp.name}"`);
  }

  async function deleteCustomPalette(id, name) {
    if (!window.confirm(`Delete "${name}" from your saved palettes?`)) return;
    const next = customPalettes.filter((p) => p.id !== id);
    await persistCustomPalettes(next, `Deleted "${name}"`);
  }

  return (
    <section className="bg-card border border-border p-8 md:p-12" data-testid="theme-section">
      {/* ── Editorial header ────────────────────────────────────────── */}
      <div className="flex items-start justify-between gap-6 mb-10 pb-8 border-b border-border">
        <div className="max-w-2xl">
          <div className="flex items-center gap-2 mb-3 text-[10px] uppercase tracking-[0.32em] text-muted-foreground font-sans">
            <Sparkles className="w-3 h-3 text-[#C9A961]" />
            <span>Studio palette</span>
          </div>
          <h2 className="font-serif text-3xl md:text-4xl tracking-tight leading-tight text-foreground">
            Theme &amp; colors
          </h2>
          <p className="text-sm text-muted-foreground mt-3 leading-relaxed">
            Choose a curated preset, then refine any single color. Every change previews live across this
            Studio Manager and your public booking site.
          </p>
        </div>
        {hasOverrides && (
          <button
            type="button"
            onClick={resetOverrides}
            className="inline-flex items-center gap-2 px-4 h-10 border border-border hover:border-foreground text-[10px] uppercase tracking-[0.28em] text-muted-foreground hover:text-foreground transition-colors shrink-0"
            data-testid="theme-reset-overrides"
            title="Clear color overrides and use the preset's defaults"
          >
            <RotateCcw className="w-3.5 h-3.5" /> Reset overrides
          </button>
        )}
      </div>

      {/* ── Premium dropdown selector ───────────────────────────────── */}
      <div className="mb-12">
        <div className="text-[10px] uppercase tracking-[0.32em] text-muted-foreground font-sans mb-3">
          Preset palette
        </div>
        <div className="relative" ref={pickerRef}>
          <button
            type="button"
            onClick={() => setPickerOpen((v) => !v)}
            data-testid="theme-picker-trigger"
            aria-haspopup="listbox"
            aria-expanded={pickerOpen}
            className="group w-full border border-border hover:border-foreground/60 bg-background transition-colors text-left flex items-stretch overflow-hidden"
          >
            {/* Swatch strip — visual anchor */}
            <div className="flex w-44 md:w-56 shrink-0">
              <div className="flex-1" style={{ background: currentTheme.palette.background }} />
              <div className="flex-[1.5]" style={{ background: currentTheme.palette.primary }} />
              <div className="flex-1" style={{ background: currentTheme.palette.accent }} />
              <div className="flex-1" style={{ background: currentTheme.palette.muted }} />
              <div className="flex-1" style={{ background: currentTheme.palette.foreground }} />
            </div>
            {/* Label & description */}
            <div className="flex-1 min-w-0 px-5 md:px-7 py-5 flex items-center justify-between gap-4">
              <div className="min-w-0">
                <div className="flex items-baseline gap-3 mb-1">
                  <span className="font-serif text-xl md:text-2xl tracking-tight text-foreground truncate">
                    {currentTheme.name}
                  </span>
                  {hasOverrides && (
                    <span
                      className="text-[9px] uppercase tracking-[0.28em] font-sans px-2 py-0.5 border border-[#C9A961] text-[#C9A961] shrink-0"
                      data-testid="theme-customized-tag"
                    >
                      Customized
                    </span>
                  )}
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed line-clamp-1">
                  {currentTheme.description}
                </p>
              </div>
              <ChevronDown
                className={`w-4 h-4 text-muted-foreground group-hover:text-foreground transition-all shrink-0 ${
                  pickerOpen ? "rotate-180" : ""
                }`}
              />
            </div>
          </button>

          {/* Dropdown panel — editorial popover */}
          {pickerOpen && (
            <div
              className="absolute z-30 mt-2 left-0 right-0 bg-card border border-border shadow-[0_24px_60px_rgb(0,0,0,0.08)] max-h-[28rem] overflow-y-auto"
              data-testid="theme-picker-panel"
              role="listbox"
            >
              {/* ── Your saved palettes (custom forks) ────────────── */}
              {customPalettes.length > 0 && (
                <>
                  <div className="px-5 py-3 border-b border-border bg-[#C9A961]/5 sticky top-0 backdrop-blur-sm flex items-center gap-2">
                    <Sparkles className="w-3 h-3 text-[#C9A961]" />
                    <div className="text-[10px] uppercase tracking-[0.32em] text-foreground font-sans">
                      Your palettes · {customPalettes.length}
                    </div>
                  </div>
                  <ul className="divide-y divide-border" data-testid="custom-palette-list">
                    {customPalettes.map((cp) => {
                      // A custom palette is "active" when its 6-key snapshot matches
                      // the currently-resolved palette exactly. Compare hex values.
                      const active =
                        cp.palette.background === resolved.background &&
                        cp.palette.foreground === resolved.foreground &&
                        cp.palette.primary === resolved.primary &&
                        cp.palette.primary_foreground === resolved.primary_foreground &&
                        cp.palette.accent === resolved.accent &&
                        cp.palette.muted === resolved.muted;
                      return (
                        <li key={cp.id} className="relative group/cp">
                          <button
                            type="button"
                            onClick={() => pickCustomPalette(cp)}
                            data-testid={`custom-palette-${cp.id}`}
                            role="option"
                            aria-selected={active}
                            className={`w-full text-left flex items-stretch transition-colors ${
                              active ? "bg-muted/60" : "hover:bg-muted/40"
                            }`}
                          >
                            <div className="flex w-32 md:w-40 shrink-0">
                              <div className="flex-1" style={{ background: cp.palette.background }} />
                              <div className="flex-[1.5]" style={{ background: cp.palette.primary }} />
                              <div className="flex-1" style={{ background: cp.palette.accent }} />
                              <div className="flex-1" style={{ background: cp.palette.muted }} />
                              <div className="flex-1" style={{ background: cp.palette.foreground }} />
                            </div>
                            <div className="flex-1 min-w-0 px-5 py-4 flex items-center justify-between gap-4">
                              <div className="min-w-0 pr-8">
                                <div className="font-serif text-base md:text-lg tracking-tight text-foreground truncate">
                                  {cp.name}
                                </div>
                                <p className="text-[11px] text-muted-foreground leading-relaxed line-clamp-1 mt-0.5">
                                  Forked from {getThemeById(cp.based_on || DEFAULT_THEME_ID).name}
                                </p>
                              </div>
                              {active && (
                                <span className="inline-flex items-center justify-center w-6 h-6 bg-[#C9A961] text-stone-900 shrink-0">
                                  <Check className="w-3.5 h-3.5" />
                                </span>
                              )}
                            </div>
                          </button>
                          {/* Delete control — hover-revealed, top-right */}
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteCustomPalette(cp.id, cp.name);
                            }}
                            data-testid={`delete-custom-palette-${cp.id}`}
                            title={`Delete "${cp.name}"`}
                            className="absolute top-3 right-3 w-7 h-7 flex items-center justify-center border border-border bg-card text-muted-foreground hover:text-destructive hover:border-destructive transition-colors opacity-0 group-hover/cp:opacity-100 focus:opacity-100"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </>
              )}

              <div className="px-5 py-3 border-b border-border bg-muted/30 sticky top-0 backdrop-blur-sm">
                <div className="text-[10px] uppercase tracking-[0.32em] text-muted-foreground font-sans">
                  {THEMES.length} curated palettes
                </div>
              </div>
              <ul className="divide-y divide-border">
                {THEMES.map((t) => {
                  const active = t.id === currentThemeId;
                  return (
                    <li key={t.id}>
                      <button
                        type="button"
                        onClick={() => pickTheme(t.id)}
                        data-testid={`theme-card-${t.id}`}
                        role="option"
                        aria-selected={active}
                        className={`group w-full text-left flex items-stretch transition-colors ${
                          active ? "bg-muted/60" : "hover:bg-muted/40"
                        }`}
                      >
                        {/* Swatch column */}
                        <div className="flex w-32 md:w-40 shrink-0">
                          <div className="flex-1" style={{ background: t.palette.background }} />
                          <div className="flex-[1.5]" style={{ background: t.palette.primary }} />
                          <div className="flex-1" style={{ background: t.palette.accent }} />
                          <div className="flex-1" style={{ background: t.palette.muted }} />
                          <div className="flex-1" style={{ background: t.palette.foreground }} />
                        </div>
                        {/* Label */}
                        <div className="flex-1 min-w-0 px-5 py-4 flex items-center justify-between gap-4">
                          <div className="min-w-0">
                            <div className="font-serif text-base md:text-lg tracking-tight text-foreground truncate">
                              {t.name}
                            </div>
                            <p className="text-[11px] text-muted-foreground leading-relaxed line-clamp-1 mt-0.5">
                              {t.description}
                            </p>
                          </div>
                          {active && (
                            <span
                              className="inline-flex items-center justify-center w-6 h-6 bg-foreground text-background shrink-0"
                              data-testid={`theme-active-badge-${t.id}`}
                            >
                              <Check className="w-3.5 h-3.5" />
                            </span>
                          )}
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      </div>

      {/* ── Editorial live preview (full width) ─────────────────────── */}
      <div className="mb-12">
        <div className="text-[10px] uppercase tracking-[0.32em] text-muted-foreground font-sans mb-3">
          Live preview
        </div>
        <div
          className="border border-border overflow-hidden"
          style={{ background: resolved.background, color: resolved.foreground }}
          data-testid="theme-mock-preview"
        >
          <div
            className="px-8 md:px-12 py-10 md:py-14 border-b"
            style={{ borderColor: shiftHexLightness(resolved.background, -8) }}
          >
            <div
              className="text-[10px] uppercase tracking-[0.32em] mb-4 font-sans"
              style={{ color: resolved.accent }}
            >
              {currentTheme.name}
            </div>
            <div
              className="font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight"
              style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
            >
              {s.studio_name || "Your studio"}
            </div>
            <p
              className="text-sm md:text-base mt-5 max-w-xl leading-relaxed"
              style={{ opacity: 0.7 }}
            >
              This is how a paragraph of body copy will read on your public site.
              Pick colors that feel like your brand, then let the rest follow.
            </p>
            <div className="flex flex-wrap gap-3 mt-7">
              <button
                type="button"
                className="px-5 h-11 text-[11px] uppercase tracking-[0.28em] font-sans"
                style={{ background: resolved.primary, color: resolved.primary_foreground }}
                data-testid="theme-preview-primary-btn"
              >
                Book a session
              </button>
              <button
                type="button"
                className="px-5 h-11 text-[11px] uppercase tracking-[0.28em] font-sans border"
                style={{
                  borderColor: shiftHexLightness(resolved.background, -18),
                  color: resolved.foreground,
                }}
              >
                Learn more
              </button>
            </div>
          </div>
          <div
            className="px-8 md:px-12 py-5 text-xs font-sans flex items-center gap-3"
            style={{ background: resolved.muted, color: resolved.foreground }}
          >
            <span
              className="inline-block w-1 h-4"
              style={{ background: resolved.accent }}
            />
            <span style={{ opacity: 0.75 }}>
              Muted surfaces sit beneath the About, Contact, and Footer sections of your site.
            </span>
          </div>
        </div>
      </div>

      {/* ── Per-color editor ────────────────────────────────────────── */}
      <div data-testid="theme-overrides-editor">
        <div className="flex items-baseline justify-between gap-4 mb-1">
          <div className="text-[10px] uppercase tracking-[0.32em] text-muted-foreground font-sans">
            Customize colors
          </div>
          <div className="flex items-center gap-4 shrink-0">
            <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-sans">
              {hasOverrides
                ? `${Object.keys(currentOverrides).filter((k) => currentOverrides[k]).length} override${
                    Object.keys(currentOverrides).filter((k) => currentOverrides[k]).length === 1 ? "" : "s"
                  }`
                : "Using preset defaults"}
            </div>
            <button
              type="button"
              onClick={openSaveDialog}
              disabled={savingPalette}
              data-testid="save-as-palette-btn"
              className="inline-flex items-center gap-1.5 px-3 h-8 border border-[#C9A961] text-[#C9A961] hover:bg-[#C9A961]/10 disabled:opacity-50 text-[10px] uppercase tracking-[0.28em] font-medium transition-colors"
              title="Save the current palette so you can reuse it later"
            >
              <Sparkles className="w-3 h-3" />
              Save as palette
            </button>
          </div>
        </div>
        <p className="text-xs text-muted-foreground mb-5 max-w-2xl leading-relaxed">
          Each swatch shows the preset&apos;s color. Override just one and the rest stay on the preset — perfect
          for swapping a single accent without losing the curated harmony.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2">
          {THEME_KEYS.map(({ key, label }) => {
            const presetColor = getThemeById(currentThemeId).palette[key];
            const overrideColor = currentOverrides[key];
            const effective = overrideColor || presetColor;
            return (
              <div
                key={key}
                className="flex items-center gap-3 py-2 border-b border-border/60"
                data-testid={`theme-override-row-${key}`}
              >
                <label
                  className="relative w-10 h-10 border border-border shrink-0 cursor-pointer overflow-hidden group/swatch"
                  style={{ background: effective }}
                  title={`${label} — click to pick`}
                >
                  <input
                    type="color"
                    value={effective}
                    onChange={(e) => setOverride(key, e.target.value)}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    aria-label={`${label} color picker`}
                    data-testid={`theme-override-color-${key}`}
                  />
                  <span className="absolute inset-0 ring-1 ring-inset ring-black/5 opacity-0 group-hover/swatch:opacity-100 transition-opacity" />
                </label>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium text-foreground leading-tight">{label}</div>
                  <div className="text-[10px] text-muted-foreground mt-0.5 leading-tight">
                    {overrideColor ? (
                      <span className="text-[#C9A961]">Override active</span>
                    ) : (
                      <>Preset</>
                    )}
                  </div>
                </div>
                <input
                  type="text"
                  value={effective}
                  onChange={(e) => setOverride(key, e.target.value)}
                  placeholder={presetColor}
                  className="w-24 text-[11px] font-mono uppercase border border-border bg-background px-2 py-1.5 focus:outline-none focus:border-foreground transition-colors shrink-0"
                  data-testid={`theme-override-hex-${key}`}
                />
                {overrideColor ? (
                  <button
                    type="button"
                    onClick={() => setOverride(key, "")}
                    className="text-muted-foreground hover:text-foreground shrink-0 transition-colors"
                    title="Clear override"
                    data-testid={`theme-override-clear-${key}`}
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                ) : (
                  <span className="w-3.5 shrink-0" />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Save-as-palette dialog (editorial portal-style) ─────────── */}
      {saveDialogOpen && (
        <div
          className="fixed inset-0 z-50 bg-background/80 backdrop-blur-xl flex items-center justify-center p-4"
          data-testid="save-palette-dialog"
          onClick={(e) => {
            if (e.target === e.currentTarget) setSaveDialogOpen(false);
          }}
        >
          <div className="bg-card border border-border shadow-2xl w-full max-w-lg p-10 md:p-12 relative animate-in fade-in zoom-in-95 duration-300">
            <button
              type="button"
              onClick={() => setSaveDialogOpen(false)}
              className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>

            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.32em] text-[#C9A961] font-sans mb-4">
                <Sparkles className="w-3 h-3" />
                Save palette
              </div>
              <h3 className="font-serif text-3xl md:text-4xl tracking-tight leading-tight text-foreground">
                Name your palette.
              </h3>
              <p className="text-sm text-muted-foreground mt-3 max-w-sm mx-auto leading-relaxed">
                We&apos;ll save these six colors as a one-click preset you can reuse across seasons or sub-brands.
              </p>
            </div>

            {/* Live swatch preview of what's being saved */}
            <div className="flex h-10 border border-border mb-8">
              <div className="flex-1" style={{ background: resolved.background }} />
              <div className="flex-[1.5]" style={{ background: resolved.primary }} />
              <div className="flex-1" style={{ background: resolved.accent }} />
              <div className="flex-1" style={{ background: resolved.muted }} />
              <div className="flex-1" style={{ background: resolved.foreground }} />
            </div>

            <div className="mb-2 text-[10px] uppercase tracking-[0.28em] text-muted-foreground font-sans">
              Palette name
            </div>
            <input
              type="text"
              value={paletteName}
              onChange={(e) => setPaletteName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") confirmSavePalette();
              }}
              placeholder="e.g. Autumn editorial"
              autoFocus
              maxLength={48}
              data-testid="save-palette-name-input"
              className="w-full border-b border-border bg-transparent px-0 py-3 font-serif text-2xl focus:outline-none focus:border-foreground transition-colors"
            />

            <div className="flex items-center gap-3 mt-10">
              <button
                type="button"
                onClick={() => setSaveDialogOpen(false)}
                className="flex-1 h-12 border border-border hover:border-foreground text-[10px] uppercase tracking-[0.28em] text-muted-foreground hover:text-foreground transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmSavePalette}
                disabled={savingPalette || !paletteName.trim()}
                data-testid="save-palette-confirm-btn"
                className="flex-1 h-12 bg-[#C9A961] text-stone-900 text-[10px] uppercase tracking-[0.28em] font-medium hover:opacity-90 disabled:opacity-50 transition-opacity inline-flex items-center justify-center gap-2"
              >
                {savingPalette ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                {savingPalette ? "Saving" : "Save palette"}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

/* ── IdentityVoiceSection ─────────────────────────────────────────────────
   Studio identity inputs: name, tagline, typeface pairing. All three feed
   the Live Preview card on the right and mark the save bar dirty. Hairline
   editorial styling — underline-only inputs, gold tracking-widest labels. */
/* TemplateFieldsSection — surfaces per-template editorial chrome that
   would otherwise be hardcoded inside each template (Vista's Issue No
   stamp + coordinates, Heirloom's Volume inscription, Brutalist's
   FRAME metadata, Spotlight's Feature No). Renders ONLY when the picked
   template ships fields here, so the Branding tab stays calm for
   studios on chrome-less templates.

   Storage: site_settings.template_fields is a Dict[template_slug,
   Dict[field_key, value]]. Each template namespace is independent so
   adding a new editable stamp to a future template is a 1-line schema
   change (just add it to TEMPLATE_FIELD_SCHEMA below) with zero migration.

   The save bar still owns commit semantics — every field edit calls
   set("template_fields", {...all-templates}) on the parent draft. */
const TEMPLATE_FIELD_SCHEMA = {
  vista: {
    label: "Vista · Magazine cover stamps",
    intro: "Edit the masthead chrome on your Vista hero — the Issue No and coordinates that read across the top and bottom of the cover photo.",
    fields: [
      { key: "issue_number", label: "Issue number", placeholder: "07", hint: "Appears as “Issue №<value>” top-left of the cover." },
      { key: "coordinates", label: "Coordinates", placeholder: "40°43′N · 74°00′W", hint: "Decorative location stamp printed bottom-left of the cover." },
    ],
  },
  heirloom: {
    label: "Heirloom · Album inscription",
    intro: "Edit the spine inscription printed on the caramel ribbon at the top of your Heirloom site (“Volume · I — 2026”).",
    fields: [
      { key: "volume", label: "Volume", placeholder: "I", hint: "Roman numeral or text — appears as “Volume · <value>” on the ribbon header AND in the footer spine." },
      { key: "year", label: "Year", placeholder: String(new Date().getFullYear()), hint: "Defaults to the current year if left blank." },
    ],
  },
  brutalist: {
    label: "Brutalist · Technical metadata",
    intro: "Edit the EXIF stamps overlaid on your Brutalist hero photo (filename, ISO, aperture, dimensions). Use your real shoot specs to lean into the engineering aesthetic.",
    fields: [
      { key: "frame_filename", label: "Frame filename", placeholder: "FRAME_001.RAW", hint: "Yellow chip in the top-left corner of the hero photo." },
      { key: "dimensions", label: "Dimensions", placeholder: "3200 × 2000 PX", hint: "Right-aligned tag below the hero — typically the resolution at which you deliver." },
      { key: "iso", label: "ISO", placeholder: "400", hint: "Without the ISO prefix — we add it." },
      { key: "aperture", label: "Aperture", placeholder: "F/2.8", hint: "Include the F/ — e.g. F/1.8, F/4." },
    ],
  },
  spotlight: {
    label: "Spotlight · Marquee chrome",
    intro: "Edit the cinema-marquee stamps on your Spotlight hero (the “Now Showing” banner and the Feature № tag).",
    fields: [
      { key: "now_showing", label: "Now-showing banner text", placeholder: "Now Showing", hint: "Top-left stamp on the hero (e.g. Premiering Summer, Limited Engagement)." },
      { key: "feature_number", label: "Feature number", placeholder: "01", hint: "Top-right stamp on the hero. Renders as Feature №<value>." },
    ],
  },
};

export function TemplateFieldsSection({ s, set }) {
  const tplId = s.template_id || "editorial";
  const schema = TEMPLATE_FIELD_SCHEMA[tplId];
  // No schema for this template = no editable chrome = hide entirely.
  // Keeps the Branding tab calm for studios on Editorial / Bold / etc.
  if (!schema) return null;

  const allFields = s.template_fields || {};
  const myFields = allFields[tplId] || {};

  function updateField(key, value) {
    set("template_fields", {
      ...allFields,
      [tplId]: { ...myFields, [key]: value },
    });
  }

  return (
    <section className="space-y-6 border-l-2 border-[color:var(--gold-base)]/40 pl-6 py-2" data-testid="template-fields-section">
      <div>
        <div className="text-[10px] uppercase tracking-[0.42em] mb-2 text-[color:var(--gold-base)]">
          {schema.label}
        </div>
        <h3 className="font-serif text-xl">Template-specific copy</h3>
        <p className="text-xs text-muted-foreground mt-2 max-w-2xl leading-relaxed">
          {schema.intro}
          <br />
          <span className="text-muted-foreground/70">
            Only visible while {TEMPLATES[tplId]?.label || tplId} is the active template — switch templates and these fields swap automatically.
          </span>
        </p>
      </div>
      <div className="grid sm:grid-cols-2 gap-5">
        {schema.fields.map((f) => (
          <Field key={f.key} label={f.label}>
            <input
              value={myFields[f.key] ?? ""}
              onChange={(e) => updateField(f.key, e.target.value)}
              placeholder={f.placeholder}
              data-testid={`template-field-${tplId}-${f.key}`}
              className="input"
            />
            {f.hint && (
              <p className="text-[10px] text-muted-foreground/70 mt-1.5">
                {f.hint}
              </p>
            )}
          </Field>
        ))}
      </div>
    </section>
  );
}


export function IdentityVoiceSection({ s, set }) {
  const currentPair = s.typeface_pair || "cormorant-manrope";
  const currentPairData = TYPEFACE_PAIRS.find((p) => p.id === currentPair) || TYPEFACE_PAIRS[0];
  const [typefaceBrowseOpen, setTypefaceBrowseOpen] = useState(false);
  return (
    <section className="space-y-9" data-testid="branding-identity-section">
      <div>
        <div className="text-[10px] uppercase tracking-[0.32em] text-[color:var(--gold-base)] mb-2 inline-flex items-center gap-1.5">
          <Sparkles className="w-3 h-3" /> Identity & voice
        </div>
        <h2 className="font-serif text-3xl italic font-light leading-tight">Studio identity</h2>
        <p className="text-sm text-muted-foreground mt-2 max-w-xl">
          The name on the door, the line on the wall, and the letters they're set in.
        </p>
      </div>

      {/* STUDIO NAME — hairline underline input */}
      <div className="space-y-2">
        <label
          htmlFor="studio-name-input"
          className="block text-[10px] uppercase tracking-[0.32em] text-[color:var(--gold-base)]"
        >
          Studio name
        </label>
        <input
          id="studio-name-input"
          type="text"
          value={s.studio_name || ""}
          onChange={(e) => set("studio_name", e.target.value)}
          placeholder="Your studio's name"
          data-testid="branding-studio-name-input"
          className="w-full max-w-lg bg-transparent border-0 border-b border-border focus:border-[color:var(--gold-base)] focus:outline-none px-0 py-2 text-2xl font-serif italic placeholder:text-muted-foreground/50 placeholder:font-serif placeholder:italic transition-colors"
        />
      </div>

      {/* TAGLINE — hairline underline input */}
      <div className="space-y-2">
        <label
          htmlFor="studio-tagline-input"
          className="block text-[10px] uppercase tracking-[0.32em] text-[color:var(--gold-base)]"
        >
          Tagline
        </label>
        <input
          id="studio-tagline-input"
          type="text"
          value={s.tagline || ""}
          onChange={(e) => set("tagline", e.target.value)}
          placeholder="A short line that captures your voice"
          data-testid="branding-tagline-input"
          className="w-full max-w-lg bg-transparent border-0 border-b border-border focus:border-[color:var(--gold-base)] focus:outline-none px-0 py-2 text-lg placeholder:text-muted-foreground/50 placeholder:italic transition-colors"
        />
        <p className="text-[11px] font-serif italic text-muted-foreground/70">
          Appears on the public site under your studio name and in the Live Preview to the right.
        </p>
      </div>

      {/* WORDMARK — hairline underline input. Renders in serif italic. */}
      <div className="space-y-2">
        <label
          htmlFor="studio-wordmark-input"
          className="block text-[10px] uppercase tracking-[0.32em] text-[color:var(--gold-base)]"
        >
          Wordmark
        </label>
        <input
          id="studio-wordmark-input"
          type="text"
          value={s.wordmark || ""}
          onChange={(e) => set("wordmark", e.target.value)}
          placeholder={s.studio_name || "ALLURE"}
          data-testid="branding-wordmark-input"
          className="w-full max-w-lg bg-transparent border-0 border-b border-border focus:border-[color:var(--gold-base)] focus:outline-none px-0 py-2 text-2xl font-serif italic placeholder:text-muted-foreground/50 placeholder:font-serif placeholder:italic transition-colors"
        />
        <p className="text-[11px] font-serif italic text-muted-foreground/70">
          The display lockup — often a stylised version of your studio name (e.g. <span className="not-italic">ALLURE</span>). Leave blank to use the studio name.
        </p>
      </div>

      {/* ACCENT COLOR — preset swatches + Custom picker */}
      <AccentColorPicker s={s} set={set} />

      {/* SITE TEMPLATE intentionally lives in its own <TemplateSection> at the
          top of the Branding tab — duplicated dropdown removed in favor of the
          premium card grid. */}

      {/* TYPEFACE PAIRING — collapsed-hero + expand-on-click pattern.
          Mirrors TemplateSection so the Branding tab reads calmly: the
          chosen specimen sits front-and-centre, the remaining options
          stay one click away. */}
      <div className="space-y-3" data-testid="branding-typeface-block">
        <span className="block text-[10px] uppercase tracking-[0.32em] text-[color:var(--gold-base)]">
          Typeface pairing
        </span>

        {/* Currently active specimen (always visible) */}
        <div
          className="relative p-5 md:p-6 border border-border bg-muted/20 overflow-hidden"
          data-testid="typeface-current-card"
        >
          <div className="flex flex-col md:flex-row md:items-end gap-5">
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-[0.32em] text-muted-foreground mb-2">
                Currently active
              </div>
              <div
                className="text-3xl md:text-4xl italic leading-none mb-3 text-foreground"
                style={{ fontFamily: currentPairData.serif }}
                data-testid={`typeface-active-${currentPairData.id}`}
              >
                {currentPairData.serifLabel}
              </div>
              <div
                className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground"
                style={{ fontFamily: currentPairData.sans }}
              >
                + {currentPairData.sansLabel}
              </div>
            </div>
            <div className="shrink-0">
              <button
                type="button"
                onClick={() => setTypefaceBrowseOpen((o) => !o)}
                aria-expanded={typefaceBrowseOpen}
                data-testid="typeface-browse-toggle"
                className="inline-flex items-center justify-between gap-3 px-5 h-11 border border-[color:var(--gold-base)]/60 text-[10px] uppercase tracking-[0.32em] text-[color:var(--gold-base)] hover:bg-[color:var(--gold-base)]/10 transition-colors"
              >
                <span>
                  {typefaceBrowseOpen
                    ? "Hide other typefaces"
                    : `Browse ${TYPEFACE_PAIRS.length - 1} other typefaces`}
                </span>
                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${typefaceBrowseOpen ? "rotate-180" : ""}`} />
              </button>
            </div>
          </div>
        </div>

        {/* Expandable grid (closed by default) */}
        {typefaceBrowseOpen && (
          <div
            className="grid sm:grid-cols-3 gap-3"
            role="radiogroup"
            aria-label="Typeface pairing"
            data-testid="branding-typeface-group"
          >
            {TYPEFACE_PAIRS.map((pair) => {
              const active = currentPair === pair.id;
              return (
                <button
                  key={pair.id}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  onClick={() => { set("typeface_pair", pair.id); setTypefaceBrowseOpen(false); }}
                  data-testid={`branding-typeface-${pair.id}`}
                  className={`text-left p-4 border transition-colors ${
                    active
                      ? "border-[color:var(--gold-base)] bg-[color:var(--gold-base)]/5"
                      : "border-border hover:border-[color:var(--gold-base)]/50"
                  }`}
                >
                  <div className="text-2xl italic leading-none mb-2" style={{ fontFamily: pair.serif }}>
                    {pair.serifLabel}
                  </div>
                  <div className="text-[11px] uppercase tracking-[0.18em]" style={{ fontFamily: pair.sans }}>
                    + {pair.sansLabel}
                  </div>
                  {active && (
                    <div className="mt-3 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.22em] text-[color:var(--gold-base)]">
                      <Check className="w-3 h-3" /> Selected
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}
